/**

 * 916 / 8815 `setGifLight` 全局串行闸门。

 * 律动桥每帧会新建 adapter，须在 SDK 层合并为单通道，避免 HID 堆积导致取消勾选后恢复变慢。

 */

import { BEIYING_8815_GIF_LIGHT_RGB_LENGTH } from "./buildBeiying9168815GifLightRgb";

let sendInFlight = false;

let aborted = false;

let pendingDirty = false;

let activeSendPromise: Promise<void> | null = null;

/** 发送代际：重新开启律动时脱离上一轮未完成的 HID promise */

let sendEpoch = 0;

/** 待发送帧（律动期间反复覆写，仅保留最新） */

const pendingRgb = new Uint8Array(BEIYING_8815_GIF_LIGHT_RGB_LENGTH);

/** 当前 HID 写入快照（与 pending 分离，避免 await 期间被覆写） */

const sendWorkRgb = new Array<number>(BEIYING_8815_GIF_LIGHT_RGB_LENGTH).fill(
  0
);

function stagePendingRgb(rgbData: number[]): void {
  const n = Math.min(rgbData.length, pendingRgb.length);

  for (let i = 0; i < n; i += 1) {
    pendingRgb[i] = rgbData[i] & 255;
  }

  for (let i = n; i < pendingRgb.length; i += 1) {
    pendingRgb[i] = 0;
  }

  pendingDirty = true;
}

function snapshotPendingToSendWork(): number[] {
  for (let i = 0; i < pendingRgb.length; i += 1) {
    sendWorkRgb[i] = pendingRgb[i];
  }

  return sendWorkRgb;
}

export function isBeiying9168815GifLightSendAborted(): boolean {
  return aborted;
}

/** 取消勾选 / 恢复快照前：丢弃待发送帧并阻止新帧入队 */

export function abortBeiying9168815GifLightSend(): void {
  aborted = true;

  pendingDirty = false;
}

export function resetBeiying9168815GifLightSendAbort(): void {
  sendEpoch += 1;

  aborted = false;

  pendingDirty = false;

  sendInFlight = false;

  activeSendPromise = null;
}

export function isBeiying9168815GifLightSendInFlight(): boolean {
  return sendInFlight;
}

/** 等待当前 HID 写入结束（abort 后不再处理 pending） */

export async function waitForBeiying9168815GifLightSendIdle(
  timeoutMs = 1200
): Promise<void> {
  const p = activeSendPromise;

  if (!p) return;

  await Promise.race([
    p.catch(() => undefined),

    new Promise<void>((resolve) => setTimeout(resolve, timeoutMs)),
  ]);
}

async function drain(
  sendFn: (rgbData: number[]) => Promise<void>,

  epoch: number
): Promise<void> {
  sendInFlight = true;

  try {
    while (pendingDirty && !aborted && epoch === sendEpoch) {
      pendingDirty = false;

      const batch = snapshotPendingToSendWork();

      await sendFn(batch);
    }
  } finally {
    if (epoch === sendEpoch) {
      sendInFlight = false;

      activeSendPromise = null;

      if (!aborted && pendingDirty) {
        activeSendPromise = drain(sendFn, epoch);

        void activeSendPromise;
      }
    }
  }
}

/**

 * 入队一帧 RGB；飞行中仅覆写 pending，HID 完成后只发最新一帧。

 */

export function enqueueBeiying9168815GifLightSend(
  sendFn: (rgbData: number[]) => Promise<void>,

  rgbData: number[]
): void {
  if (aborted) return;

  if (!rgbData?.length) return;

  stagePendingRgb(rgbData);

  if (sendInFlight) return;

  activeSendPromise = drain(sendFn, sendEpoch);

  void activeSendPromise;
}
